import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RouterStateService } from '../root/router-state.service';
import { SimpleFinn } from '../utilities/simpleFinn';

export interface OCRBOLLocation {
  locationValueTypeCode: string;
  locationValue: number[];
}

export interface BOLOCR {
  ocrId: number;
  orderId: number;
  bolNumber?: string;
  purchaseOrderNumber?: string;
  actualLoadWeight?: number;
  actualLoadWeightUnit?: string;
  startedAtLocalTime?: string;
  completedAtLocalTime?: string;
  bolOriginalImageFilename: string;
  bolOriginalImageBucket: string;
  bolOriginalImageUrl?: string;
  auditPassed?: boolean;
  auditRequestedBy?: number;
  auditRequestedTimestamp?: string;
  auditCompletedBy?: number;
  auditCompletedTimestamp?: string;
  auditConductedAt?: string;
  notesInternal?: string;
  ocrProcessedTimestamp?: string;
  ocrDataLocations?: OCRBOLLocation[];
  createdTimestamp: string;
}

export interface AuditResultsPayload {
  ocrId: number;
  orderId: number;
  auditorPhoneNumber: string;
  auditConductedAt: string;
  auditPassed: boolean;
  notesInternal: string;
}

const BOLOCR_ORDER_ID_ROUTE_PARAM = 'bolocrOrderId';

@Injectable({
  providedIn: 'root',
})
export class BolOcrAuditService {
  private page = 0;

  private allBOLOCRs: SimpleFinn<BOLOCR[]>;
  private failedBOLOCRs: SimpleFinn<BOLOCR[]>;

  private orderBOLOCR$$ = new BehaviorSubject<BOLOCR | null>(null);
  public orderBOLOCR$ = this.orderBOLOCR$$.asObservable().pipe(shareReplay(1));

  public getAllBOLOCRs$(): Observable<BOLOCR[]> {
    return this.allBOLOCRs.get$();
  }

  public getFailedBOLOCRs$(): Observable<BOLOCR[]> {
    return this.failedBOLOCRs.get$();
  }

  constructor(private httpClient: HttpClient, private routerState: RouterStateService) {
    this.allBOLOCRs = new SimpleFinn<BOLOCR[]>([], this.loadAllBOLOCRs.bind(this));
    this.failedBOLOCRs = new SimpleFinn<BOLOCR[]>([], this.loadFailedBOLOCRs.bind(this));
    this.listenForBOLOCROrder();
  }

  public setPage(page: number): void {
    this.page = page;
  }

  private getRequestParams(): HttpParams {
    let params = new HttpParams();
    params = params.append('qty', '25');
    params = params.append('page', `${this.page}`);
    params = params.append('desc', 'true');
    params = params.append('sortby', 'createdTimestamp');

    return params;
  }

  private async loadAllBOLOCRs(): Promise<BOLOCR[]> {
    const params = this.getRequestParams();

    return this.httpClient
      .get<BOLOCR[]>(`${environment.api}/v2/internal/ocr/list_ocr_bols`, { params })
      .pipe(
        map((bolocrs) => {
          if (!bolocrs) {
            return [];
          } else {
            return bolocrs;
          }
        }),
      )
      .toPromise();
  }

  private async loadFailedBOLOCRs(): Promise<BOLOCR[]> {
    const params = this.getRequestParams();

    return this.httpClient
      .get<BOLOCR[]>(`${environment.api}/v2/internal/ocr/failed_ocr_bols`, { params })
      .pipe(
        map((bolocrs) => {
          if (!bolocrs) {
            return [];
          } else {
            return bolocrs;
          }
        }),
      )
      .toPromise();
  }

  private listenForBOLOCROrder() {
    this.routerState.listenForParamChange$(BOLOCR_ORDER_ID_ROUTE_PARAM).subscribe(async (orderID: string) => {
      if (!!orderID) {
        const orderBOLOCR = await this.httpClient
          .get<BOLOCR>(`${environment.api}/v2/internal/ocr/${orderID}`)
          .toPromise();
        this.orderBOLOCR$$.next(orderBOLOCR);
      } else {
        this.orderBOLOCR$$.next(null);
      }
    });
  }
}
